import React, { useState, useEffect } from 'react';
import { Settings, Server, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { API_ENVIRONMENTS, API_CONFIG } from '@/lib/config/api-config';
import { toast } from '@/hooks/use-toast';
import { clearSession } from '@/services/authService';

// Function to clear session data while preserving the API override setting
const clearSessionExceptApiOverride = () => {
  const apiOverride = localStorage.getItem('dev-api-override');
  
  // Use the same session clearing logic as logout
  clearSession();
  
  // Restore API override setting
  if (apiOverride) {
    localStorage.setItem('dev-api-override', apiOverride);
  }
};

export const ApiEnvironmentSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentEnvironment, setCurrentEnvironment] = useState<string>('');
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    // Get current environment from localStorage or default
    const saved = localStorage.getItem('dev-api-override');
    if (saved && Object.values(API_ENVIRONMENTS).includes(saved as any)) {
      setCurrentEnvironment(saved);
    } else {
      setCurrentEnvironment(API_ENVIRONMENTS.DEVELOPMENT);
    }
  }, []);

  const handleEnvironmentChange = async (newEnvironment: string) => {
    if (newEnvironment === currentEnvironment) return;
    
    setIsChanging(true);
    
    try {
      // Test the new environment by making a simple request
      const testResponse = await fetch(`${newEnvironment}/health`);
      
      if (testResponse.ok || testResponse.status === 404) {
        // 404 is acceptable as not all APIs have a health endpoint
        localStorage.setItem('dev-api-override', newEnvironment);
        setCurrentEnvironment(newEnvironment);
        
        // Clear all localStorage except the API override setting to avoid conflicts
        clearSessionExceptApiOverride();
        
        toast({
          title: "API Environment Changed",
          description: `Switched to ${getEnvironmentName(newEnvironment)}. Clearing local storage and reloading...`,
          variant: "default",
        });
        
        // Reload the page to apply changes
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        throw new Error(`HTTP ${testResponse.status}`);
      }
    } catch (error) {
      console.warn('API environment test failed:', error);
      
      // Still allow the switch but show a warning
      localStorage.setItem('dev-api-override', newEnvironment);
      setCurrentEnvironment(newEnvironment);
      
      // Clear all localStorage except the API override setting to avoid conflicts
      clearSessionExceptApiOverride();
      
      toast({
        title: "API Environment Changed (Warning)",
        description: `Switched to ${getEnvironmentName(newEnvironment)} but couldn't verify connectivity. Clearing local storage and reloading...`,
        variant: "default",
      });
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
    
    setIsChanging(false);
    setIsOpen(false);
  };

  const getEnvironmentName = (url: string): string => {
    switch (url) {
      case API_ENVIRONMENTS.DEVELOPMENT:
        return 'Development';
      case API_ENVIRONMENTS.STAGING:
        return 'Staging';
      case API_ENVIRONMENTS.PRODUCTION:
        return 'Production';
      default:
        return 'Custom';
    }
  };

  const getEnvironmentBadgeVariant = (url: string) => {
    switch (url) {
      case API_ENVIRONMENTS.DEVELOPMENT:
        return 'default';
      case API_ENVIRONMENTS.STAGING:
        return 'secondary';
      case API_ENVIRONMENTS.PRODUCTION:
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const clearOverride = () => {
    localStorage.removeItem('dev-api-override');
    toast({
      title: "API Override Cleared",
      description: "Using default environment configuration. Page will reload.",
      variant: "default",
    });
    
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // Only show in development mode
  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="fixed bottom-4 right-4 z-50 bg-background border-2 shadow-lg h-9 px-3"
        >
          <Server className="h-4 w-4 mr-2" />
          API: {getEnvironmentName(currentEnvironment)}
          <Badge 
            variant={getEnvironmentBadgeVariant(currentEnvironment)} 
            className="ml-2 text-xs"
          >
            DEV
          </Badge>
        </Button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            API Environment Switcher
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Switch between different API environments for testing. Changes take effect after page reload.
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Current API Base URL:</Label>
            <div className="font-mono text-xs bg-muted p-2 rounded">
              {API_CONFIG.BASE_URL}
            </div>
          </div>
          
          <RadioGroup
            value={currentEnvironment}
            onValueChange={handleEnvironmentChange}
            disabled={isChanging}
          >
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={API_ENVIRONMENTS.DEVELOPMENT} id="dev" />
                <Label htmlFor="dev" className="flex-1">
                  <div className="flex items-center justify-between">
                    <span>Development</span>
                    <Badge variant="default" className="text-xs">
                      dev-api.nodus.foo
                    </Badge>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={API_ENVIRONMENTS.STAGING} id="staging" />
                <Label htmlFor="staging" className="flex-1">
                  <div className="flex items-center justify-between">
                    <span>Staging</span>
                    <Badge variant="secondary" className="text-xs">
                      nodus.foo
                    </Badge>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={API_ENVIRONMENTS.PRODUCTION} id="prod" />
                <Label htmlFor="prod" className="flex-1">
                  <div className="flex items-center justify-between">
                    <span>Production</span>
                    <Badge variant="destructive" className="text-xs">
                      api.nodus.se
                    </Badge>
                  </div>
                </Label>
              </div>
            </div>
          </RadioGroup>
          
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearOverride}
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="flex-1"
            >
              Close
            </Button>
          </div>
          
          {isChanging && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Testing connection...
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
